'use client';
import { Modal } from '@benzinga/core-ui';
import React from 'react';
import { ConfigSelectorOption, ConfigSelectorTab } from './entities';
import { toTitleCase } from '@benzinga/utils';
import styled, { css } from '@benzinga/themetron';
import { FaRegEdit, FaRegStar, FaRegTrashAlt, FaSearch, FaStar } from 'react-icons/fa';
import { props } from 'ramda';

const TabBar: React.FC<{
  tabs: ConfigSelectorTab[];
  selectedTabId: string;
  onSelectTab: (tabId: string) => void;
}> = props => {
  const tabs = React.useMemo(() => {
    const onSelectTab = props.onSelectTab;
    return props.tabs.map(tab => ({
      ...tab,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelectTab(tab.id);
      },
    }));
  }, [props.onSelectTab, props.tabs]);
  return (
    <TabBarWrapper>
      {tabs.map(tab => (
        <Tab
          key={tab.id}
          onClick={tab.onClick}
          selected={tab.id === props.selectedTabId}
          title={tab.tooltipText ?? tab.displayText}
        >
          {tab.displayText}
        </Tab>
      ))}
    </TabBarWrapper>
  );
};

const Search: React.FC<{
  value: string;
  onClick: (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => void;
  onChange: (value: string) => void;
  onNewSave: (name: string) => void;
  resourceName: string;
}> = props => {
  const onChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    e => {
      const onChange = props.onChange;
      onChange(e.target.value);
    },
    [props],
  );

  const onClick = React.useCallback(
    (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
      const onClick = props.onClick;
      onClick(e);
    },
    [props],
  );
  const onNewSave = React.useMemo(() => {
    const onSave = props.onNewSave;
    return () => onSave(props.value);
  }, [props.onNewSave, props.value]);
  const isDisabled = React.useMemo(() => props.value.length === 0, [props.value.length]);
  return (
    <SearchWrapper>
      <SearchBoxWrapper>
        <SearchIcon />
        <SearchInput
          autoFocus
          onChange={onChange}
          onClick={onClick}
          placeholder={`${toTitleCase(props.resourceName)} name`}
          type="text"
          value={props.value}
        />
      </SearchBoxWrapper>
      {
        <NewButtonWrapper>
          <NewButton
            disabled={isDisabled}
            onClick={onNewSave}
            title={isDisabled ? `Enter ${props.resourceName} name` : `Create a new ${props.resourceName}`}
          >{`CREATE A NEW ${props.resourceName.toUpperCase()}`}</NewButton>
        </NewButtonWrapper>
      }
    </SearchWrapper>
  );
};

const EditingConfigListItem: React.FC<{
  onEditNameSave: (name: string) => void;
  onEditNameCancel: () => void;
  name: string;
  id: string;
}> = props => {
  const [currentName, setCurrentName] = React.useState(props.name);
  const onKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
    e => {
      const onEditNameSave = props.onEditNameSave;
      const onEditNameCancel = props.onEditNameCancel;
      switch (e.key) {
        case 'Enter': {
          onEditNameSave(currentName);
          break;
        }
        case 'Escape': {
          onEditNameCancel();
          break;
        }
      }
    },
    [currentName, props.onEditNameCancel, props.onEditNameSave],
  );
  return (
    <ConfigListItemWrapper key={props.id} selected={true}>
      <ConfigListItemInput
        autoFocus={true}
        onChange={e => setCurrentName(e.target.value)}
        onKeyDown={onKeyDown}
        value={currentName}
      />
    </ConfigListItemWrapper>
  );
};

const ConfigListItem: React.FC<{
  onFavorite?: (e: React.MouseEvent) => void;
  onEditNameClick?: (e: React.MouseEvent) => void;
  onRemove: (e: React.MouseEvent) => void;
  onSelect: () => void;
  displayText: string;
  tooltipText?: string | undefined;
  favorite?: boolean | undefined;
  id: string;
  selectedConfig: ConfigSelectorOption | undefined;
}> = props => {
  return (
    <ConfigListItemWrapper
      key={props.id}
      onClick={props.onSelect}
      selected={props.id === props.selectedConfig?.id}
      title={props.tooltipText ?? props.displayText}
    >
      {props.onFavorite &&
        (props.favorite ? (
          <ConfigListItemStar onClick={props.onFavorite}>
            <FaStar />
          </ConfigListItemStar>
        ) : (
          <ConfigListItemStarReg onClick={props.onFavorite}>
            <FaRegStar />
          </ConfigListItemStarReg>
        ))}
      <ConfigListItemText>{props.displayText}</ConfigListItemText>
      {props.onEditNameClick && (
        <ConfigListItemEditName className="displayOnHover" onClick={props.onEditNameClick}>
          <FaRegEdit />
        </ConfigListItemEditName>
      )}
      <ConfigListItemRemove className="displayOnHover" onClick={props.onRemove}>
        <FaRegTrashAlt />
      </ConfigListItemRemove>
    </ConfigListItemWrapper>
  );
};

const ConfigList: React.FC<{
  onFavorite?: (id: string, favorite: boolean) => void;
  onNameEdit?: (id: string, newName: string) => void;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  searchText: string;
  selectedTab: ConfigSelectorTab;
  selectedConfig: ConfigSelectorOption | undefined;
}> = props => {
  const [editingConfigId, setEditingConfigId] = React.useState<string | undefined>(undefined);
  const configList = React.useMemo(() => {
    const onSelect = props.onSelect;
    const onFavorite = props.onFavorite;
    const onRemove = props.onRemove;
    const onNameEdit = props.onNameEdit;
    return props.selectedTab
      .getOptions()
      .filter(config => config.displayText.toLowerCase().includes(props.searchText.toLowerCase()))
      .map(c => ({
        ...c,
        onEditNameCancel:
          onNameEdit &&
          (() => {
            setEditingConfigId(undefined);
          }),
        onEditNameClick:
          onNameEdit &&
          ((e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            setEditingConfigId(c.id);
          }),
        onEditNameSave:
          onNameEdit &&
          ((name: string) => {
            onNameEdit(c.id, name);
            setEditingConfigId(undefined);
          }),
        onFavorite: onFavorite
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              setEditingConfigId(undefined);
              onFavorite?.(c.id, !c.favorite);
            }
          : undefined,
        onRemove: (e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
          setEditingConfigId(undefined);
          onRemove(c.id);
        },
        onSelect: () => {
          setEditingConfigId(undefined);
          onSelect?.(c.id);
        },
      }));
  }, [props.onFavorite, props.onNameEdit, props.onRemove, props.onSelect, props.searchText, props.selectedTab]);
  return (
    <ConfigListWrapper>
      {configList.map(c => {
        if (c.id === editingConfigId && c.onEditNameSave && c.onEditNameCancel) {
          return (
            <EditingConfigListItem
              id={c.id}
              key={c.id}
              name={c.displayText}
              onEditNameCancel={c.onEditNameCancel}
              onEditNameSave={c.onEditNameSave}
            />
          );
        }
        return (
          <ConfigListItem {...c} key={c.id} onEditNameClick={c.onEditNameClick} selectedConfig={props.selectedConfig} />
        );
      })}
    </ConfigListWrapper>
  );
};

export interface ConfigSelectorModalProps {
  resourceName: string;
  selectedConfig: ConfigSelectorOption | undefined;
  modalParentRef?: HTMLDivElement | null;
  onFavorite?: (id: string, favorite: boolean) => void;
  onNameEdit?: (id: string, newName: string) => void;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  tabs: ConfigSelectorTab[];
  isOpen: boolean;
  onClose: () => void;
  onNewSave: (name: string) => void;
}

export const ConfigSelectorModal: React.FC<ConfigSelectorModalProps> = props => {
  const [state, setState] = React.useState<{ selectedTabId: string | undefined; searchText: string }>(() => ({
    searchText: '',
    selectedTabId: props.tabs.at(0)?.id,
  }));

  const selectedTab = React.useMemo(() => {
    const tab = props.tabs.find(tab => tab.id === state.selectedTabId);
    if (!tab) {
      throw new Error(`Tab with id ${state.selectedTabId} not found`);
    }
    return tab;
  }, [props.tabs, state.selectedTabId]);

  React.useEffect(() => {
    if (!props.isOpen) return;
    setState(s => ({ ...s, searchText: '' }));
  }, [props.isOpen, props.tabs]);

  const handleContentClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Modal
      onClose={props.onClose}
      parentRef={props.modalParentRef}
      size={{ alert: true }}
      title={`${toTitleCase(props.resourceName)}s`}
      visible={props.isOpen}
    >
      <ModalContent onClick={handleContentClick}>
        {state.selectedTabId && (
          <TabBar
            onSelectTab={(tabId: string) => setState(s => ({ ...s, selectedTabId: tabId }))}
            selectedTabId={state.selectedTabId}
            tabs={props.tabs}
          />
        )}
        <Search
          onChange={text => {
            setState(s => ({ ...s, searchText: text }));
          }}
          onClick={e => {
            e.stopPropagation();
          }}
          onNewSave={props.onNewSave}
          resourceName={props.resourceName}
          value={state.searchText}
        />
        <ConfigList
          onFavorite={props.onFavorite}
          onNameEdit={props.onNameEdit}
          onRemove={props.onRemove}
          onSelect={props.onSelect}
          searchText={state.searchText}
          selectedConfig={props.selectedConfig}
          selectedTab={selectedTab}
        />
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TabBarWrapper = styled.div`
  display: flex;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding-left: 16px;
  padding-top: 8px;
  gap: 4px;
`;

const Tab = styled.div<{ selected: boolean }>`
  padding: 6px 12px;
  cursor: pointer;
  font-weight: 500;
  ${props => {
    if (props.selected) {
      return css`
        background-color: ${props => props.theme.colors.border};
        color: ${props.theme.colors.foregroundActive};
        border-top: 2px solid ${props.theme.colors.brand};
      `;
    } else {
      return css`
        background-color: ${props => props.theme.colors.background};
        color: ${props.theme.colors.foregroundInactive};
        border-top: 2px solid ${`${props.theme.colors.brand}60`};
        &:hover {
          border-top: 2px solid ${props.theme.colors.brand};
          background-color: ${props => `${props.theme.colors.backgroundInactive}80`};
          color: ${props => props.theme.colors.foregroundActive};
        }
      `;
    }
  }}
`;

const SearchWrapper = styled.div`
  height: 34px;
  gap: 8px;
  display: flex;
  width: 100%;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const SearchBoxWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  gap: 8px;
  padding: 9px 0px 9px 16px;
`;

const SearchInput = styled.input`
  border: none;
  width: 100%;
  flex-grow: 1;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.foregroundActive};
  padding: 0px;
  font-size: 14px;
  line-height: 20px;
  &:focus {
    outline: none;
  }
  ::placeholder {
    color: ${props => props.theme.colors.foregroundInactive};
  }
`;

const SearchIcon = styled(FaSearch)`
  fill: ${props => props.theme.colors.foregroundInactive};
`;

const NewButtonWrapper = styled.div`
  display: flex;
  border-left: solid 1px ${props => props.theme.colors.border};
  padding: 2px;
  height: 100%;
`;

const NewButton = styled.button`
  padding: 10px 15px;
  height: 100%;
  font-weight: 500;
  color: ${props => props.theme.colors.foregroundInactive};
  ${props =>
    props.disabled
      ? css`
          cursor: unset;
          background-color: ${props => props.theme.colors.backgroundActive};
        `
      : css`
          cursor: pointer;
          background-color: ${props => props.theme.colors.brand + 'B0'};
          &:hover {
            background-color: ${props => props.theme.colors.brand};
          }
        `}
`;

const ConfigListWrapper = styled.div`
  display: flex;
  gap: 2px;
  padding: 14px 12px;
  flex-direction: column;
  height: 30vh;
  overflow-y: auto;
`;

const ConfigListItemWrapper = styled.div<{ selected?: boolean }>`
  gap: 4px;
  cursor: pointer;
  display: flex;
  border-radius: 4px;
  padding: 6px;
  .displayOnHover {
    display: none;
  }
  &:hover {
    .displayOnHover {
      display: unset;
    }
  }
  ${props =>
    props.selected
      ? css`
          background-color: ${props => props.theme.colors.border};
        `
      : css`
          &:hover {
            background-color: ${props => props.theme.colors.border};
          }
        `}
`;

const ConfigListItemStar = styled.span`
  svg {
    min-width: 16px;
    fill: ${props => props.theme.colorPalette.yellow400};
  }
`;
const ConfigListItemStarReg = styled.span`
  svg {
    min-width: 16px;
    fill: ${props => props.theme.colors.foreground};
  }
  &:hover {
    svg {
      fill: ${props => props.theme.colorPalette.yellow400};
    }
  }
`;
const ConfigListItemRemove = styled.span`
  svg {
    min-width: 16px;
    fill: ${props => props.theme.colors.foreground};
  }
  &:hover {
    svg {
      fill: ${props => props.theme.colors.danger};
    }
  }
`;
const ConfigListItemEditName = styled.span`
  svg {
    min-width: 16px;
    fill: ${props => props.theme.colors.foreground};
  }
  &:hover {
    svg {
      fill: ${props => props.theme.colors.foregroundActive};
    }
  }
`;

const ConfigListItemText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-grow: 1;
`;

const ConfigListItemInput = styled.input`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: unset;
  padding: 0 0 0 5px;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.foreground};
  outline: none !important;
  font-size: 12px;
  flex-grow: 1;
`;
